import { getRowKey } from '../';
import { PardotFieldRow } from './PardotFieldRow';
import { PardotFieldSearch } from './PardotFieldSearch';
import { PERISCOPE_TAB_TYPES } from '@sonar-software/sonar-ts-constants';
import { SelectionTable } from '@ui/tables';
import { v3FieldSortOptions } from '../fields/FieldSortOptions';
import {
	ApiCodeToggleQueryParams,
	SearchFilterQueryParams,
	SortByFilterQueryParams,
	TagFilterQueryParams
} from '../filters';
import { KeyPagination, keyPaginationDefaultQueryParams, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import {
	KeyPaginationQueryParams,
	OffsetPaginatedResource,
	PeriscopePardotField,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import React, { useCallback, useRef } from 'react';
import { useQueryResource, useSelectionTable } from '@hooks';

type Resource = OffsetPaginatedResource<PeriscopePardotField>;
export type PardotFieldSearchableSelectionTableV3QueryParams = ApiCodeToggleQueryParams &
	KeyPaginationQueryParams &
	SearchFilterQueryParams &
	SortByFilterQueryParams &
	TagFilterQueryParams;

export interface PardotFieldSearchableSelectionTableV3Props {
	parentIndex: number;
	resourceIdsUri: string;
	showParents?: boolean;
	uri: string;
}

export const PardotFieldSearchableSelectionTableV3: React.FC<PardotFieldSearchableSelectionTableV3Props> = ({
	parentIndex,
	resourceIdsUri,
	showParents,
	uri
}) => {
	const scrollableListRef = useRef<HTMLDivElement>(null);

	const { loading, resource, queryParams, updateParams } = useQueryResource<
		Resource,
		PardotFieldSearchableSelectionTableV3QueryParams
	>({
		defaultParams: {
			...keyPaginationDefaultQueryParams,
			sortBy: v3FieldSortOptions[ 0 ].key,
			sortDirection: v3FieldSortOptions[ 0 ].direction
		},
		fallbackParams: keyPaginationFallbackQueryParams,
		uri
	});

	const selectionData = useSelectionTable({
		getRowKey,
		queryParams,
		resourceIdsUri,
		results: resource?.results,
		selectionType: SonarObjectType.PardotField
	});

	const renderField = useCallback(
		(field: PeriscopePardotField) =>
			<PardotFieldRow
				field={ field }
				key={ field.sonarObjectId }
				parentIndex={ parentIndex }
				showParents={ showParents } />
		,
		[ parentIndex, showParents ]
	);

	const results = resource?.results;

	return (
		<>
			<PardotFieldSearch
				onChange={ updateParams }
				queryParams={ queryParams } />
			<SelectionTable
				dataSource={ results }
				loading={ loading }
				ref={ scrollableListRef }
				renderRow={ renderField }
				rowKey={ getRowKey }
				selectionData={ selectionData }
				selectionType={ PERISCOPE_TAB_TYPES.FIELDS } />
			<KeyPagination
				defaultPageSize={ Number(queryParams.count) }
				listRef={ scrollableListRef }
				onChange={ updateParams }
				resource={ resource } />
		</>
	);
};
